import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../Revenue/revenue.css";
import "./appVersions.css";
import { errorMessage } from "../Revenue/revenueApi";
import { Loading } from "../Revenue/parts";
import {
  APP_STRINGS,
  AV_LANGS,
  BLOCK_WARN_SHARE,
  LIVE_MIN_HITS,
  MESSAGE_MAX,
  NOTES_MAX,
  NOTE_MAX_CHARS,
  PLATFORMS,
  TITLE_MAX,
  actionFor,
  blockedHits,
  diffPayload,
  fmtAgo,
  fmtDateTime,
  fmtInt,
  isHttpsUrl,
  listAppVersions,
  newlyBlockedHits,
  pct,
  saveAppVersion,
  toForm,
  toPayload,
  totalHits,
} from "./appVersionsApi";

const langOf = (code) => AV_LANGS.find((l) => l.code === code) || AV_LANGS[0];

const ACTION_BADGE = {
  force: { cls: "rv-badge-red", text: "Blocked" },
  soft: { cls: "rv-badge-gold", text: "Update sheet" },
  none: { cls: "rv-badge-green", text: "OK" },
};

function ActionBadge({ action }) {
  const b = ACTION_BADGE[action] || ACTION_BADGE.none;
  return <span className={`rv-badge ${b.cls}`}>{b.text}</span>;
}

function Switch({ checked, onChange, label, hint, danger = false }) {
  return (
    <label className={`av-switch-row ${danger ? "danger" : ""}`}>
      <span className="av-switch">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="av-switch-track" />
      </span>
      <span>
        <span className="av-switch-label">{label}</span>
        {hint && <span className="av-switch-hint">{hint}</span>}
      </span>
    </label>
  );
}

/// Force / soft app update settings: one card per platform with the build
/// gates, the store link, the texts in every app language, a live preview of
/// what users see, the builds seen in the last 24 h and how many of them a
/// change would block. Saving shows a before/after review; risky changes
/// (server 409 warnings) need an explicit "Save anyway".
export default function AppVersions() {
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await listAppVersions();
      setRows(res?.data || []);
      setMeta(res?.meta || {});
    } catch (e) {
      setRows([]);
      setError(errorMessage(e, "Could not load the app version settings"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const byPlatform = useMemo(() => Object.fromEntries((rows || []).map((r) => [r.platform, r])), [rows]);

  return (
    <div className="rv-wrap av-page">
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
        <div>
          <h2 className="rv-title mb-1">App versions</h2>
          <p className="rv-sub">
            Force or suggest app updates per platform. Builds below the minimum are blocked; builds below the latest
            see a dismissible update sheet. Never raise the minimum above the build that is live in the store.
          </p>
        </div>
        <button type="button" className="rv-btn ghost" onClick={load}>
          Refresh
        </button>
      </div>

      {notice && <div className="rv-success mb-3">{notice}</div>}
      {error && <div className="rv-error mb-3">{error}</div>}
      {rows && meta && meta.schema_ready === false && (
        <div className="rv-error mb-3">
          The database is not fully set up for this page: run <b>DB_APP_VERSIONS_2026_10_22.sql</b> (live build,
          release notes, builds seen, change log). Until then those parts stay empty.
        </div>
      )}

      {rows === null ? (
        <Loading />
      ) : (
        <div className="av-cards">
          {PLATFORMS.map((p) => (
            <PlatformCard
              key={p.key}
              platform={p}
              row={byPlatform[p.key]}
              onSaved={(msg) => {
                setNotice(msg);
                load();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PlatformCard({ platform, row, onSaved }) {
  const [form, setForm] = useState(() => toForm(row));
  const [lang, setLang] = useState("en");
  const [previewLang, setPreviewLang] = useState("en");
  const [reviewing, setReviewing] = useState(false);

  useEffect(() => {
    setForm(toForm(row));
  }, [row]);

  const defaults = row?.defaults || {};
  const seen = useMemo(() => row?.seen_24h || [], [row]);
  const original = useMemo(() => toPayload(toForm(row)), [row]);
  const payload = useMemo(() => toPayload(form), [form]);
  const diff = useMemo(() => diffPayload(original, payload), [original, payload]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setText = (field, code, value) => setForm((f) => ({ ...f, [field]: { ...f[field], [code]: value } }));
  const setNote = (code, i, value) =>
    setForm((f) => {
      const list = [...(f.release_notes[code] || [])];
      list[i] = value;
      return { ...f, release_notes: { ...f.release_notes, [code]: list } };
    });

  const maxSeen = row?.max_seen_build ?? null;
  const liveBuild = Number.isInteger(payload.live_build) ? payload.live_build : null;
  const effectiveLive = liveBuild ?? maxSeen;
  const savedCfg = row?.exists ? original : null;
  const total = totalHits(seen);
  const blockedNow = blockedHits(seen, savedCfg);
  const blockedAfter = blockedHits(seen, payload);
  const newly = newlyBlockedHits(seen, savedCfg, payload);

  // Same refusals as the server (the server stays the authority).
  const problems = [];
  const intOk = (v) => Number.isInteger(v) && v >= 0;
  if (!intOk(payload.min_build)) problems.push("Minimum build must be a whole number.");
  if (!intOk(payload.latest_build)) problems.push("Latest build must be a whole number.");
  if (payload.live_build !== null && !intOk(payload.live_build)) problems.push("Live build must be a whole number.");
  if (payload.store_url && !isHttpsUrl(payload.store_url)) problems.push("The store link must be an https:// address.");
  if (intOk(payload.min_build) && intOk(payload.latest_build) && payload.min_build > payload.latest_build) {
    problems.push("The minimum build cannot be higher than the latest build.");
  }
  if (payload.enabled && effectiveLive !== null && intOk(payload.min_build) && payload.min_build > effectiveLive) {
    problems.push(
      `The minimum build (${payload.min_build}) is above the build live in the store (${effectiveLive}): users would be blocked with nothing to install.`
    );
  }
  if (payload.enabled && payload.force_latest && effectiveLive !== null && payload.latest_build > effectiveLive) {
    problems.push(`"Force latest" is on but the latest build (${payload.latest_build}) is not live in the store (${effectiveLive}).`);
  }
  AV_LANGS.forEach(({ code }) => {
    if ((form.title[code] || "").length > TITLE_MAX) problems.push(`Title (${code}) is longer than ${TITLE_MAX} characters.`);
    if ((form.message[code] || "").length > MESSAGE_MAX || (form.force_message[code] || "").length > MESSAGE_MAX) {
      problems.push(`A message (${code}) is longer than ${MESSAGE_MAX} characters.`);
    }
    if ((form.release_notes[code] || []).some((s) => (s || "").trim().length > NOTE_MAX_CHARS)) {
      problems.push(`A release note (${code}) is longer than ${NOTE_MAX_CHARS} characters.`);
    }
  });

  const heads = [];
  if (payload.enabled && liveBuild !== null && payload.latest_build > liveBuild) {
    heads.push(`The latest build (${payload.latest_build}) is not confirmed live in the store yet (live: ${liveBuild}).`);
  }
  if (payload.enabled && liveBuild === null) {
    heads.push(
      maxSeen !== null
        ? `No live build confirmed — the highest build seen ≥ ${LIVE_MIN_HITS} times in 24 h (${maxSeen}) is used instead.`
        : "No live build confirmed and not enough checks seen to know it. Enter the build that is live in the store."
    );
  }
  if (total > 0 && newly / total > BLOCK_WARN_SHARE) {
    heads.push(`This change blocks ${pct(newly, total)}% of the checks of the last 24 h.`);
  }

  const status = !row?.exists
    ? { cls: "rv-badge-grey", text: "Not set up" }
    : !row.enabled
    ? { cls: "rv-badge-grey", text: "Prompts off" }
    : { cls: "rv-badge-green", text: "Active" };

  return (
    <div className="rv-panel av-card">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <h3 className="av-card-title">{platform.label}</h3>
          <span className={`rv-badge ${status.cls}`}>{status.text}</span>
        </div>
        <div className="rv-run-meta">Saved {row?.updated_at ? fmtDateTime(row.updated_at) : "never"}</div>
      </div>

      <Switch
        checked={form.enabled}
        onChange={(v) => set("enabled", v)}
        label="Update prompts enabled"
        hint="Kill switch: off = the app never blocks or suggests an update on this platform."
        danger={!form.enabled}
      />

      <div className="av-grid mt-3">
        <BuildField
          label="Minimum build"
          hint="Older builds are blocked"
          build={form.min_build}
          version={form.min_version}
          onBuild={(v) => set("min_build", v)}
          onVersion={(v) => set("min_version", v)}
        />
        <BuildField
          label="Latest build"
          hint="Older builds see the update sheet"
          build={form.latest_build}
          version={form.latest_version}
          onBuild={(v) => set("latest_build", v)}
          onVersion={(v) => set("latest_version", v)}
        />
        <BuildField
          label={`Live in ${platform.store}`}
          hint={
            row?.live_confirmed_at
              ? `Confirmed ${fmtDateTime(row.live_confirmed_at)}`
              : "The build users can download now"
          }
          build={form.live_build}
          version={form.live_version}
          placeholder={maxSeen !== null ? `seen: ${maxSeen}` : "not confirmed"}
          onBuild={(v) => set("live_build", v)}
          onVersion={(v) => set("live_version", v)}
          extra={
            maxSeen !== null && String(maxSeen) !== form.live_build ? (
              <button type="button" className="rv-btn ghost rv-btn-sm mt-2" onClick={() => set("live_build", String(maxSeen))}>
                Use highest seen ({maxSeen})
              </button>
            ) : null
          }
        />
      </div>

      <div className="mt-3">
        <Switch
          checked={form.force_latest}
          onChange={(v) => set("force_latest", v)}
          label="Force latest"
          hint="Also block every build below the latest build (not only below the minimum)."
          danger={form.force_latest}
        />
      </div>

      <div className="mt-3">
        <label className="rv-label">Store link</label>
        <div className="d-flex gap-2">
          <input
            className="rv-input"
            value={form.store_url}
            placeholder="https://"
            onChange={(e) => set("store_url", e.target.value)}
          />
          <a
            className={`rv-btn ghost av-open ${isHttpsUrl(form.store_url) ? "" : "disabled"}`}
            href={isHttpsUrl(form.store_url) ? form.store_url.trim() : undefined}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => !isHttpsUrl(form.store_url) && e.preventDefault()}
          >
            Open
          </a>
        </div>
        {platform.key === "ios" && (
          <div className="av-hint">The app opens the App Store app directly (itms-apps) and falls back to this link.</div>
        )}
      </div>

      <div className="av-section-title">Texts</div>
      <div className="av-lang-tabs" role="tablist">
        {AV_LANGS.map((l) => {
          const filled =
            form.title[l.code] || form.message[l.code] || form.force_message[l.code] || (form.release_notes[l.code] || []).some(Boolean);
          return (
            <button
              key={l.code}
              type="button"
              className={`av-lang ${lang === l.code ? "active" : ""}`}
              onClick={() => {
                setLang(l.code);
                setPreviewLang(l.code);
              }}
            >
              {l.code.toUpperCase()}
              {filled && <i className="av-dot" />}
            </button>
          );
        })}
      </div>
      <TextsEditor
        lang={langOf(lang)}
        form={form}
        defaults={defaults}
        onText={setText}
        onNote={setNote}
      />

      <div className="av-section-title d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span>Live preview</span>
        <select className="rv-select av-preview-lang" value={previewLang} onChange={(e) => setPreviewLang(e.target.value)}>
          {AV_LANGS.map((l) => (
            <option key={l.code} value={l.code}>
              {l.native}
            </option>
          ))}
        </select>
      </div>
      <Previews lang={langOf(previewLang)} form={form} defaults={defaults} />

      <div className="av-section-title">Builds seen in the last 24 h</div>
      <SeenTable row={row} seen={seen} savedCfg={savedCfg} payload={payload} />
      {seen.length > 0 && (
        <div className="av-impact">
          <div>
            Blocked now: <b>{pct(blockedNow, total)}%</b> ({fmtInt(blockedNow)} of {fmtInt(total)})
          </div>
          <div className={newly / Math.max(total, 1) > BLOCK_WARN_SHARE ? "av-impact-bad" : ""}>
            % blocked by this change: <b>{pct(blockedAfter, total)}%</b>
            {newly > 0 && <> (+{pct(newly, total)}% newly blocked)</>}
          </div>
        </div>
      )}

      {problems.length > 0 && (
        <div className="rv-error mt-3">
          {problems.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </div>
      )}
      {problems.length === 0 && diff.length > 0 && heads.length > 0 && (
        <div className="av-warn mt-3">
          {heads.map((h) => (
            <div key={h}>{h}</div>
          ))}
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
        <button type="button" className="rv-btn ghost" disabled={!diff.length} onClick={() => setForm(toForm(row))}>
          Discard
        </button>
        <button
          type="button"
          className="rv-btn"
          disabled={!diff.length || problems.length > 0}
          onClick={() => setReviewing(true)}
        >
          Review &amp; save{diff.length ? ` (${diff.length})` : ""}
        </button>
      </div>

      <RecentChanges changes={row?.recent_changes || []} />

      {reviewing && (
        <ReviewModal
          platform={platform}
          diff={diff}
          before={original}
          after={payload}
          raisesBlock={
            payload.min_build > (original.min_build || 0) || (payload.force_latest && !original.force_latest)
          }
          impact={{ total, blockedNow, blockedAfter, newly }}
          onClose={() => setReviewing(false)}
          onSaved={(msg) => {
            setReviewing(false);
            onSaved(msg);
          }}
        />
      )}
    </div>
  );
}

function BuildField({ label, hint, build, version, onBuild, onVersion, placeholder = "", extra = null }) {
  return (
    <div className="av-build">
      <label className="rv-label">{label}</label>
      <div className="d-flex gap-2">
        <input
          className="rv-input av-build-num"
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          placeholder={placeholder || "build"}
          value={build}
          onChange={(e) => onBuild(e.target.value)}
        />
        <input
          className="rv-input"
          placeholder="version e.g. 2.1.2"
          value={version}
          maxLength={20}
          onChange={(e) => onVersion(e.target.value)}
        />
      </div>
      <div className="av-hint">{hint}</div>
      {extra}
    </div>
  );
}

function TextsEditor({ lang, form, defaults, onText, onNote }) {
  const code = lang.code;
  return (
    <div className="av-texts" dir={lang.dir}>
      <label className="rv-label">Title ({lang.native})</label>
      <input
        className="rv-input"
        dir={lang.dir}
        maxLength={TITLE_MAX}
        value={form.title[code]}
        placeholder={defaults.title?.[code] || ""}
        onChange={(e) => onText("title", code, e.target.value)}
      />
      <div className="av-hint" dir="ltr">
        Empty = default. The block page uses “{defaults.force_title?.[code] || "Update required"}” when empty.
      </div>

      <label className="rv-label mt-3">Update sheet message</label>
      <textarea
        className="rv-textarea"
        rows={3}
        dir={lang.dir}
        maxLength={MESSAGE_MAX}
        value={form.message[code]}
        placeholder={defaults.message?.[code] || ""}
        onChange={(e) => onText("message", code, e.target.value)}
      />

      <label className="rv-label mt-3">Block page message</label>
      <textarea
        className="rv-textarea"
        rows={3}
        dir={lang.dir}
        maxLength={MESSAGE_MAX}
        value={form.force_message[code]}
        placeholder={defaults.force_message?.[code] || ""}
        onChange={(e) => onText("force_message", code, e.target.value)}
      />

      <label className="rv-label mt-3">
        Release notes (up to {NOTES_MAX} bullets{code !== "en" ? ", empty = English notes" : ""})
      </label>
      {(form.release_notes[code] || []).map((note, i) => (
        <div key={i} className="av-note">
          <span className="av-note-dot">•</span>
          <input
            className="rv-input"
            dir={lang.dir}
            maxLength={NOTE_MAX_CHARS}
            value={note}
            placeholder={code === "en" ? `Bullet ${i + 1}` : form.release_notes.en?.[i] || `Bullet ${i + 1}`}
            onChange={(e) => onNote(code, i, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
}

/// What the app shows, with the texts the app would pick for [lang]:
/// the override of that language, else the default.
function Previews({ lang, form, defaults }) {
  const code = lang.code;
  const s = APP_STRINGS[code] || APP_STRINGS.en;
  const own = (form.release_notes[code] || []).map((v) => v.trim()).filter(Boolean);
  const notes = own.length ? own : (form.release_notes.en || []).map((v) => v.trim()).filter(Boolean);
  const blockTitle = form.title[code]?.trim() || defaults.force_title?.[code] || "Update required";
  const sheetTitle = form.title[code]?.trim() || defaults.title?.[code] || "Update WIKI WIKI";
  const blockMsg = form.force_message[code]?.trim() || defaults.force_message?.[code] || "";
  const sheetMsg = form.message[code]?.trim() || defaults.message?.[code] || "";
  const cur = form.min_version?.trim() ? `${form.min_version.trim()}` : "2.1.0";
  const next = form.latest_version?.trim() || form.live_version?.trim() || `build ${form.latest_build || "?"}`;

  return (
    <div className="av-previews">
      <figure className="av-preview">
        <div className="av-phone" dir={lang.dir}>
          <div className="av-notch" />
          <div className="av-block">
            <div className="av-logo">W</div>
            <div className="av-p-title">{blockTitle}</div>
            <div className="av-p-msg">{blockMsg}</div>
            {notes.length > 0 && (
              <div className="av-p-notes">
                <div className="av-p-notes-h">{s.whatsNew}</div>
                {notes.map((n) => (
                  <div key={n} className="av-p-note">
                    • {n}
                  </div>
                ))}
              </div>
            )}
            <div className="av-p-safe">{s.safe}</div>
            <div className="av-p-btn">{s.update}</div>
            <div className="av-p-btn ghost">{s.checkAgain}</div>
            <div className="av-p-ver">{s.version(cur, next)}</div>
          </div>
        </div>
        <figcaption>Update required (blocking)</figcaption>
      </figure>

      <figure className="av-preview">
        <div className="av-phone" dir={lang.dir}>
          <div className="av-notch" />
          <div className="av-feed">
            <div className="av-feed-shade" />
            <div className="av-sheet">
              <div className="av-handle" />
              <div className="av-sheet-icon">⟳</div>
              <div className="av-p-title sm">{sheetTitle}</div>
              {(form.latest_version || "").trim() && <div className="av-p-ver">{form.latest_version.trim()}</div>}
              <div className="av-p-msg">{sheetMsg}</div>
              {notes.length > 0 && (
                <div className="av-p-notes">
                  {notes.map((n) => (
                    <div key={n} className="av-p-note">
                      • {n}
                    </div>
                  ))}
                </div>
              )}
              <div className="av-p-btn">{s.update}</div>
              <div className="av-p-later">{s.later}</div>
            </div>
          </div>
        </div>
        <figcaption>Update available (sheet on the feed)</figcaption>
      </figure>

      <figure className="av-preview av-preview-banner">
        <div className="av-live-banner" dir={lang.dir}>
          {s.afterLive}
        </div>
        <figcaption>Banner in a LIVE while a block waits for it to end</figcaption>
      </figure>
    </div>
  );
}

function SeenTable({ row, seen, savedCfg, payload }) {
  if (row && row.stats_ready === false) {
    return <div className="rv-empty">Run DB_APP_VERSIONS_2026_10_22.sql to record the builds that check in.</div>;
  }
  if (!seen.length) {
    return <div className="rv-empty">No checks recorded in the last 24 h yet (1 in 10 checks is sampled).</div>;
  }
  const total = totalHits(seen);
  return (
    <div className="rv-table-wrap">
      <table className="rv-table av-seen">
        <thead>
          <tr>
            <th>Build</th>
            <th className="rv-num">Checks (est.)</th>
            <th className="rv-num">Share</th>
            <th>Last seen</th>
            <th>Now</th>
            <th>After change</th>
          </tr>
        </thead>
        <tbody>
          {seen.map((r) => {
            const now = actionFor(savedCfg, r.build);
            const after = actionFor(payload, r.build);
            return (
              <tr key={r.build} className={now !== after ? "av-changed" : ""}>
                <td>
                  <b>{r.build}</b>
                  {r.hits < LIVE_MIN_HITS && <span className="av-hint ms-2">few checks</span>}
                </td>
                <td className="rv-num">{fmtInt(r.hits)}</td>
                <td className="rv-num">{pct(r.hits, total)}%</td>
                <td>{fmtAgo(r.last_seen)}</td>
                <td>
                  <ActionBadge action={now} />
                </td>
                <td>
                  <ActionBadge action={after} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function RecentChanges({ changes }) {
  const [open, setOpen] = useState(false);
  if (!changes.length) return null;
  return (
    <div className="mt-3">
      <button type="button" className="av-link" onClick={() => setOpen((o) => !o)}>
        {open ? "Hide" : "Show"} last {changes.length} changes
      </button>
      {open && (
        <div className="av-changes">
          {changes.map((c) => {
            const rows = diffPayload(c.before || {}, c.after || {}).filter((d) => d.key !== "updated_at");
            return (
              <div key={c.id} className="av-change">
                <div className="rv-run-meta">
                  {fmtDateTime(c.created_at)} · admin #{c.admin_id ?? "?"}
                  {c.warnings?.length ? ` · confirmed: ${c.warnings.join(", ")}` : ""}
                </div>
                {rows.length ? (
                  rows.map((d) => (
                    <div key={d.key} className="av-change-row">
                      <span>{d.label}</span>
                      <span className="av-before">{d.before}</span>
                      <span>→</span>
                      <span className="av-after">{d.after}</span>
                    </div>
                  ))
                ) : (
                  <div className="av-hint">{c.before ? "No field changed" : "Created"}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReviewModal({ platform, diff, before, after, raisesBlock, impact, onClose, onSaved }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState(null);

  const typedOk = !raisesBlock || typed.trim().toLowerCase() === platform.key;

  const send = async (confirm) => {
    setBusy(true);
    setError("");
    try {
      const res = await saveAppVersion(platform.key, { ...after, confirm });
      onSaved(res?.message || `${platform.label} settings saved.`);
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      if (status === 409 && Array.isArray(data.warnings)) {
        setWarnings(data.warnings);
      } else {
        setError(errorMessage(e, "Could not save"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rv-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="rv-modal av-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h4 className="rv-panel-title mb-2">Review {platform.label} change</h4>
        <div className="av-diff">
          <div className="av-diff-head">
            <span>Field</span>
            <span>Before</span>
            <span>After</span>
          </div>
          {diff.map((d) => (
            <div key={d.key} className="av-diff-row">
              <span>{d.label}</span>
              <span className="av-before">{d.before}</span>
              <span className="av-after">{d.after}</span>
            </div>
          ))}
        </div>

        {impact.total > 0 && (
          <div className="av-impact mt-2">
            <div>
              Checks of the last 24 h blocked: <b>{pct(impact.blockedNow, impact.total)}%</b> →{" "}
              <b>{pct(impact.blockedAfter, impact.total)}%</b>
            </div>
          </div>
        )}

        {raisesBlock && (
          <div className="mt-3">
            <label className="rv-label">
              This blocks more users. Type <b>{platform.key}</b> to confirm.
            </label>
            <input
              className="rv-input"
              value={typed}
              autoFocus
              onChange={(e) => setTyped(e.target.value)}
              placeholder={platform.key}
            />
            {before.live_build === null && after.live_build === null && (
              <div className="av-hint mt-1">Tip: confirm the live build first so the server can check the minimum.</div>
            )}
          </div>
        )}

        {warnings && (
          <div className="av-warn mt-3">
            <div className="mb-1">
              <b>The server asks you to confirm:</b>
            </div>
            {warnings.map((w) => (
              <div key={w.code}>• {w.message}</div>
            ))}
          </div>
        )}
        {error && <div className="rv-error mt-3">{error}</div>}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <button type="button" className="rv-btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {warnings ? (
            <button type="button" className="rv-btn danger" disabled={busy || !typedOk} onClick={() => send(true)}>
              {busy ? "Saving…" : "Save anyway"}
            </button>
          ) : (
            <button type="button" className="rv-btn" disabled={busy || !typedOk} onClick={() => send(false)}>
              {busy ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
